import { datadogLogs } from '@datadog/browser-logs';
import config from 'src/config';

// Browser logging for the unattended device screens (/floor kiosk tablets,
// /scanner handhelds) and for uncaught errors app-wide.
//
// This exists because of the /floor clock-out bug: a tap produced no request,
// no error and no visible change, and with zero client-side telemetry the only
// signal was a warehouse manager reporting it weeks later. These screens run
// unattended on hardware nobody can open a devtools console on, so anything we
// don't deliberately log is invisible.
//
// This is the logs SDK only — no RUM application, no session replay. It needs
// just a client token (write-only, safe to ship in the browser bundle). RUM
// additionally needs a Datadog-side application id, which does not exist for
// this app yet; if one is created later the two SDKs link automatically and
// these lines pick up session/view context for free.

// Only environments with a real Datadog org behind them. Local dev and the
// Cypress mock stack must never ship logs.
const LOGGED_ENVIRONMENTS = ['prod-1', 'stage-1', 'stage-2'];

let started = false;

type UserAgentData = {
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<{ platform?: string; model?: string; platformVersion?: string }>;
};

// Chrome's reduced user agent says "Android 10; K" on every Android device, so
// a CT47 handheld and a CT60 look identical in the logs (sc-67057). User-agent
// client hints carry the real model and Android version. They resolve
// asynchronously, so the first lines of a page load can arrive without them.
// Browsers without client hints (Safari, Firefox) just get no device context.
const attachDeviceContext = (): void => {
  const { userAgentData } = navigator as Navigator & {
    userAgentData?: UserAgentData;
  };
  userAgentData
    ?.getHighEntropyValues?.(['model', 'platformVersion'])
    .then(({ platform, model, platformVersion }) => {
      datadogLogs.setGlobalContextProperty('device', {
        platform,
        model,
        platformVersion,
      });
    })
    .catch(() => undefined);
};

export const initDatadogLogs = (): void => {
  const { datadog_client_token: clientToken, environment } = config();
  // No token (local dev, tests) or an unrecognised env: stay completely inert.
  if (started || !clientToken || !LOGGED_ENVIRONMENTS.includes(environment)) {
    return;
  }
  datadogLogs.init({
    clientToken,
    site: 'datadoghq.com',
    service: 'logistics-web-app',
    env: environment,
    // The SDK's default, set explicitly because it is most of the value here:
    // uncaught exceptions, unhandled rejections, console.error and failed
    // network requests all get forwarded. That covers the crashes nobody is
    // present to see on a kiosk.
    //
    // It does mean a render crash reports twice — React console.errors it, and
    // ErrorBoundary reports it deliberately. Keep both: only the deliberate one
    // carries the React component stack.
    forwardErrorsToLogs: true,
    // Internal tool with modest traffic; no need to sample away the one session
    // that actually contains the bug.
    sessionSampleRate: 100,
  });
  started = true;
  attachDeviceContext();
};

type Context = Record<string, unknown>;

// Deliberate breadcrumbs. Named `<screen>.<subject>.<outcome>` so a single
// device's story reads in order in the Logs explorer, e.g.
//   floor.clock_out.opened -> floor.clock_out.confirmed -> floor.clock_out.failed
//
// `event` is duplicated into the context so it can be faceted on; the message
// alone is only searchable as free text.
export const logEvent = (event: string, context: Context = {}): void => {
  if (!started) return;
  datadogLogs.logger.info(event, { event, ...context });
};

export const logWarning = (event: string, context: Context = {}): void => {
  if (!started) return;
  datadogLogs.logger.warn(event, { event, ...context });
};

// The SDK's third argument is what populates `@error.stack` and `@error.kind`.
// Passing the Error through it rather than stringifying it into the message is
// the difference between an error Datadog can group and a line of prose.
export const logError = (
  event: string,
  error: Error,
  context: Context = {},
): void => {
  if (!started) return;
  datadogLogs.logger.error(event, { event, ...context }, error);
};

// Exposed for tests, which need to assert the guard rather than ship logs.
export const isStarted = (): boolean => started;
export const resetForTests = (): void => {
  started = false;
};
